import { alpha, Box, styled, Typography } from '@mui/material';

import type { MutationTone } from './ConsequencesPanel.types';

export const DialogColumns = styled(Box)(({ theme }) => ({
  display: 'grid',
  // the form needs a steady column: 540px keeps the two date steppers (each
  // ~245px with the weekday) side by side inside the panel padding. The
  // consequences table gets the rest, so plan unit, work agreement and
  // mutation fit
  gridTemplateColumns: 'minmax(320px, 540px) minmax(0, 1fr)',
  gap: theme.spacing(3),
  // take the available width (also inside the roster's employee-card popup),
  // but keep a stable footprint so the centred dialog does not jump while the
  // data loads
  width: 'min(1320px, calc(100vw - 80px))',
  // a fixed height, not a minimum: unfolding the working-days box or an error
  // message must not resize the dialog under the planner's cursor. Columns
  // scroll inside instead.
  height: 'min(560px, calc(100vh - 220px))',
  // stretch, so the right column can fill the height of the left one and give
  // the consequences panel every pixel that is left over
  alignItems: 'stretch',
  // narrow viewports: stack the columns instead of squeezing them
  [theme.breakpoints.down('medium')]: {
    gridTemplateColumns: '1fr',
    width: 'calc(100vw - 48px)',
    height: 'auto'
  }
}));

export const Column = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  minHeight: 0,
  // lets the content shrink inside the grid column instead of overflowing it
  minWidth: 0
}));

/** section heading in the app's idiom: green title with a thin rule below */
export const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  paddingBottom: theme.spacing(0.5),
  // room for the floating labels of the fields right below the rule
  marginBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.primary.main}`
}));

export const SectionPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  // waiting for the employee: visible, so the planner sees what comes next,
  // but greyed and out of reach
  '&[inert]': {
    opacity: theme.palette.action.disabledOpacity
  }
}));

export const PeriodRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

export const NoticeBanner = styled(Box)(({ theme }) => ({
  background: theme.palette.info.light,
  color: theme.palette.getContrastText(theme.palette.info.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  marginBottom: theme.spacing(2),
  fontSize: 13
}));

// no frame of its own: the section panel around it already is one
export const ConsequencesBox = styled(Box)(() => ({
  // fills the panel: as many rows visible as the dialog height allows
  flex: 1,
  minHeight: 180,
  overflowY: 'auto'
}));

/**
 * columns: day | date | time | work agreement | mutation | detaches (the
 * open duty that comes back). Every track is fixed or resolves identically per row (equal fixed
 * tracks, equal fr distribution), so the rows line up as real columns across
 * the plan unit groups instead of shifting with the width of their content.
 */
const CONSEQUENCE_COLUMNS =
  '36px 52px 90px minmax(110px, 0.8fr) minmax(0, 1.4fr) 84px';
/** while editing there is no Ontkoppeld column: nothing is detached there */
const CONSEQUENCE_COLUMNS_WITHOUT_DETACHES =
  '36px 52px 90px minmax(110px, 0.8fr) minmax(0, 1.4fr)';
const columnsFor = (withDetaches?: boolean) =>
  withDetaches === false
    ? CONSEQUENCE_COLUMNS_WITHOUT_DETACHES
    : CONSEQUENCE_COLUMNS;

/**
 * duties of the same day share a background (the app's zebra colour), so the
 * borders between rows can stay away inside a group: the day boundary is the
 * colour change
 */
export const ConsequenceRow = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'shaded' && prop !== 'dimmed' && prop !== 'withDetaches'
})<{ shaded?: boolean; dimmed?: boolean; withDetaches?: boolean }>(
  ({ theme, shaded, dimmed, withDetaches }) => ({
    display: 'grid',
    gridTemplateColumns: columnsFor(withDetaches),
    alignItems: 'center',
    columnGap: theme.spacing(1),
    padding: theme.spacing(0, 1),
    // fixed height: rows with a chip or icon stay exactly as tall as rows
    // with only text
    height: 34,
    fontSize: 13,
    backgroundColor: shaded ? theme.palette.action.hover : undefined,
    // faded text: this row is not part of the save. The mutation chip keeps
    // its own colours, so "no change" still stands out
    color: dimmed ? theme.palette.text.disabled : undefined
  })
);

/** column labels above the groups, in the table-header idiom */
export const ConsequenceHeaderRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withDetaches'
})<{ withDetaches?: boolean }>(({ theme, withDetaches }) => ({
  display: 'grid',
  gridTemplateColumns: columnsFor(withDetaches),
  columnGap: theme.spacing(1),
  padding: theme.spacing(0, 1, 0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontSize: 11,
  fontWeight: 600,
  // written as is, not uppercased: the abbreviation "Dvb" must stay "Dvb"
  letterSpacing: '0.02em'
}));

export const ConsequenceCell = styled(Box)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

/** a yes/no cell with its icon in front */
export const FlagCell = styled(ConsequenceCell, {
  shouldForwardProp: (prop) => prop !== 'positive'
})<{ positive?: boolean }>(({ theme, positive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: positive ? theme.palette.success.main : theme.palette.text.secondary
}));

export const PlanUnitGroupBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5)
}));

/** the plan unit a block of duties belongs to; a button, because it folds
 *  the block in and out. A plan unit without duties has nothing to fold */
export const PlanUnitGroupHeader = styled('button', {
  shouldForwardProp: (prop) => prop !== 'muted'
})<{ muted?: boolean }>(({ theme, muted }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  width: '100%',
  padding: theme.spacing(0.5, 1),
  border: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: 'none',
  textAlign: 'left',
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  color: muted ? theme.palette.text.secondary : theme.palette.text.primary,
  '&:hover:not(:disabled)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:disabled': {
    cursor: 'default',
    color: muted ? theme.palette.text.secondary : theme.palette.text.primary
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2
  }
}));

/** the fold arrow: down when open, right when folded in */
export const PlanUnitChevron = styled('span', {
  shouldForwardProp: (prop) => prop !== 'open'
})<{ open?: boolean }>(({ theme, open }) => ({
  display: 'inline-flex',
  alignSelf: 'center',
  fontSize: 13,
  color: theme.palette.text.secondary,
  transform: open ? 'none' : 'rotate(-90deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  })
}));

/** "Toon alle 12 diensten" under a long block */
export const ShowAllButton = styled('button')(({ theme }) => ({
  display: 'block',
  padding: theme.spacing(0.75, 1),
  border: 'none',
  background: 'none',
  font: 'inherit',
  fontSize: 12,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2
  }
}));

export const PlanUnitIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignSelf: 'center',
  color: theme.palette.primary.main,
  fontSize: 13
}));

export const PlanUnitMeta = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 400
}));

/**
 * What happens to a duty, as a small tinted label. "No change" is a dark grey
 * on purpose: it has to read as a deliberate outcome, not as a faded row.
 * `muted` fades the label with its row: an unscheduled duty is no longer the
 * employee's, only its open-duty outcome still matters.
 */
export const MutationChip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'muted'
})<{ tone: MutationTone; muted?: boolean }>(({ theme, tone, muted }) => {
  const tinted = (color: string, text: string) => ({
    backgroundColor: alpha(color, 0.14),
    color: text
  });
  const colors = {
    converted: tinted(theme.palette.info.main, theme.palette.info.dark),
    detached: tinted(theme.palette.warning.main, theme.palette.warning.dark),
    created: tinted(theme.palette.success.main, theme.palette.success.dark),
    none: {
      backgroundColor: theme.palette.grey[700],
      color: theme.palette.getContrastText(theme.palette.grey[700])
    }
  }[tone];
  const fadedColors = {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.disabled
  };

  return {
    ...(muted ? fadedColors : colors),
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.25, 1),
    fontSize: 12,
    lineHeight: 1.5
  };
});

/** the totals above the table */
export const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(1.5)
}));

export const LoaderSlot = styled(Box)(({ theme }) => ({
  height: 4,
  marginBottom: theme.spacing(1)
}));

/** keeps the previous result readable while a recalculation runs */
export const Refreshing = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'refreshing'
})<{ refreshing?: boolean }>(({ refreshing }) => ({
  opacity: refreshing ? 0.6 : 1
}));

export const WorkAgreementMeta = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13
}));

/** a colour per work agreement, so tab and description belong together */
export const WORK_AGREEMENT_COLORS = [
  'primary.main',
  'info.main',
  'secondary.main',
  'warning.dark'
];

export const HintText = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  marginBottom: theme.spacing(1)
}));

export const FteRolloutBox = styled(Box)(({ theme }) => ({
  border: `1px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(1)
}));

export const InfoBox = styled(Box)(({ theme }) => ({
  background: theme.palette.info.light,
  color: theme.palette.getContrastText(theme.palette.info.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  fontSize: 13
}));

/** the inschaling changes inside the period: information, not an error */
// a light tint, not the solid info panel: the jump button has to stay readable
// on it, and a plain notice should not weigh as much as an error
export const BenefitChangeNoticeBox = styled(Box)(({ theme }) => ({
  background: alpha(theme.palette.info.main, 0.12),
  color: theme.palette.info.dark,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1.5),
  marginTop: theme.spacing(1.5),
  fontSize: 13
}));

/** one work agreement's change, with the jump to its end beside it */
export const BenefitChangeLine = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: theme.spacing(1),
  minHeight: 30
}));

export const WarningBox = styled(Box)(({ theme }) => ({
  background: theme.palette.warning.light,
  color: theme.palette.getContrastText(theme.palette.warning.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  fontSize: 13
}));

/** the no-duties advice under a plan unit that has nothing to change */
export const PlanUnitWarningBox = styled(WarningBox)(({ theme }) => ({
  margin: theme.spacing(1)
}));

/** for situations where continuing is pointless, e.g. an overlapping absence */
export const ErrorBox = styled(Box)(({ theme }) => ({
  background: theme.palette.error.light,
  color: theme.palette.getContrastText(theme.palette.error.light),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  fontSize: 13
}));

/**
 * The overlap message: red type, no filled panel. A block of colour this size
 * reads as an alarm and pushes the fields below it out of the way, while the
 * message is one the planner acts on rather than one that has to shout.
 */
export const OverlapBox = styled(Box)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 13,
  marginTop: theme.spacing(2)
}));

export const OverlapTitle = styled(Box)({
  fontWeight: 600
});

export const DialogActionsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  padding: theme.spacing(2)
}));

/**
 * A requirement that is not attached to a field of its own — the work agreement
 * choice. Said where the choice is made, because a disabled save button cannot
 * explain itself: it takes no focus, so a tooltip on it is unreachable.
 */
export const RequiredHint = styled(Box)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 13,
  marginBottom: theme.spacing(1)
}));
